import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
} from '@mui/material';
import { Field } from '../../UI';
import EmailsPadraoDialog from './EmailsPadraoDialog';
import {
  buscarInfoInicialLimparEnvioEmail, submitFormEnvioEmailEmMassa,
} from '../../../../services/core/processos/processosEnviarEmailDevedorService';

const CAMPOS_INICIAIS = {
  emailDe: '',
  // Não exibido no formulário no modo massa (mesma tela real), mas
  // `submitFormEnvioEmailEmMassa` sempre lê `formFields.emailPara` para o
  // multipart — sem essa chave, o valor vira `undefined` e `FormData` o
  // serializa como a string literal "undefined" (achado em code review).
  emailPara: '',
  emailCopia: '',
  respostaPara: '',
  assunto: '',
  nomeAdvogado: '',
  emailAdvogado: '',
  texto: '',
  nomeModelo: '',
  idTipoEmailPadrao: '',
  solicitarFase: '',
};

// Tokens literais como armazenados no `texto` vindo do backend (HTML-escapado
// pelo editor de origem) — replicados exatamente da tela real
// (`EnviarEmailDevedor.jsx`), não uma decisão do protótipo.
const TOKEN_NOME_ADVOGADO = '&lt;$NomeAdvogado$&gt;';
const TOKEN_EMAIL_ADVOGADO = '&lt;$EmailAdvogado$&gt;';
const MENSAGEM_HISTORICO_PROCESSOS = 'Consulte o historico dos Processos.';
const MENSAGEM_ERRO_GENERICA = 'Não foi possível enviar os e-mails.';
const ANEXOS_VAZIO = [];

/**
 * Porta de `DialogEnviarEmailDevedor.jsx`/`DialogEnviarEmailDevedorController.js`
 * (tela real) + modo `isAcaoEmMassa` de `EnviarEmailDevedor.jsx`, Etapa 4 (final)
 * de `docs/architecture/plano-acoes-em-massa-grid-processos.md`.
 *
 * Campo Texto é sempre `readOnly` (`Field` com `InputProps={{ readOnly: true }}`,
 * sem `onChange`) — nunca há um caminho de código que permita `setCampos`
 * alterar `texto` a partir de digitação; o único jeito de popular esse campo
 * é via `onConfirmar` de `EmailsPadraoDialog` (Etapa 3). Isso é reforço no
 * estado, não só no atributo visual — ver Vetores de Ataque do plano.
 *
 * `anexos` sempre vazio (`ANEXOS_VAZIO`, constante — upload fora de escopo
 * desta entrega, ver Padrões e Decisões do plano); sem input de arquivo.
 *
 * Sem Quill: campo Texto usa `TextField`/`Field` multiline `readOnly` puro,
 * decisão já registrada no plano (mesma renderização visual da tela real
 * neste modo, que também não permite digitação livre).
 *
 * Todo feedback (sucesso, aviso, erro) passa por `onFeedback(severity, mensagem)`,
 * implementado pelo componente pai persistente (`AcoesEmMassaMenu.jsx`) — nunca
 * por um Snackbar local aqui. Diferente de `DialogEnviarOutraFase.jsx` (Etapa 2,
 * cujo `erroGenerico` local é seguro porque nenhum caminho de erro dela fecha o
 * dialog), aqui o caminho de "histórico de processos" fecha o dialog ao mostrar
 * o aviso — um Snackbar local desapareceria junto (`Dialog` sem `keepMounted`).
 * `AcoesEmMassaMenu.jsx` precisa passar essa prop memoizada (`useCallback`) —
 * uma referência nova a cada render recria `limparFormulario` e reexecuta o
 * efeito de abertura mesmo com o dialog já aberto, apagando o formulário no
 * meio da digitação (achado em code review).
 *
 * `sessaoRef` identifica cada abertura do dialog — o envio em curso guarda o
 * valor no momento do clique e o compara antes de aplicar qualquer efeito
 * colateral (`setEnviando`, `onFeedback`, `onClose`, `setErros`). Sem isso,
 * fechar o dialog com um envio pendente e reabri-lo faria a resposta tardia
 * da sessão antiga fechar/mostrar sucesso na sessão nova (achado em code
 * review) — e o botão Enviar ficaria preso desabilitado (`enviando` nunca
 * resetado ao reabrir).
 */
export default function DialogEnviarEmailDevedor({
  open, onClose, onFeedback, processosSelecionados,
}) {
  const [campos, setCampos] = useState(CAMPOS_INICIAIS);
  const [erros, setErros] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [openEmailsPadrao, setOpenEmailsPadrao] = useState(false);
  const sessaoRef = useRef(0);
  const enviandoRef = useRef(false);
  const foiAbertoRef = useRef(false);

  // `sessaoRef` incrementa dentro da própria `limparFormulario` (fonte única
  // de invalidação) — chamá-la pelo botão "Limpar" também gera uma sessão
  // nova, então 2 cliques rápidos em "Limpar" (ou "Limpar" seguido de
  // reabertura) não deixam uma resposta antiga sobrescrever uma mais nova
  // dentro do que deveria ser a mesma sessão (achado em code review).
  const limparFormulario = useCallback(() => {
    sessaoRef.current += 1;
    const sessaoAtual = sessaoRef.current;
    setCampos(CAMPOS_INICIAIS);
    setErros({});
    setEnviando(false);
    enviandoRef.current = false;
    buscarInfoInicialLimparEnvioEmail(parseInt(processosSelecionados[0], 10))
      .then((response) => {
        if (sessaoRef.current !== sessaoAtual) return;
        setCampos(fields => ({ ...fields, emailDe: response.data.emailDe }));
      })
      .catch(() => {
        if (sessaoRef.current !== sessaoAtual) return;
        onFeedback('error', MENSAGEM_ERRO_GENERICA);
      });
  }, [processosSelecionados, onFeedback]);

  // Só reseta o formulário na transição real de fechado -> aberto
  // (`foiAbertoRef`), não sempre que `limparFormulario` muda de referência —
  // se um caller passasse `processosSelecionados` recriado a cada render
  // (array novo por render, mesmo com o mesmo conteúdo), o efeito reexecutaria
  // e apagaria o formulário em digitação mesmo com o dialog já aberto e sem
  // nenhuma nova seleção (achado em code review; não reproduzível com o
  // caller atual — `idsProcessos`/`marcados` em GridProcessos.jsx é
  // `useState`, referência estável — mas a guarda remove a dependência dessa
  // disciplina de memoização a montante).
  useEffect(() => {
    if (open && !foiAbertoRef.current) {
      limparFormulario();
    }
    foiAbertoRef.current = open;
  }, [open, limparFormulario]);

  const onChangeCampo = nome => (event) => {
    // React 16 recicla o SyntheticEvent (pooling) — com 2 setState no mesmo
    // handler, o updater de `setCampos` pode rodar fora do ciclo síncrono do
    // evento, quando `event.target` já foi zerado pelo pool. Extrair o valor
    // antes de qualquer setState evita o `Cannot read properties of null`.
    const { value } = event.target;
    setCampos(fields => ({ ...fields, [nome]: value }));
    setErros(camposErro => ({ ...camposErro, [nome]: undefined }));
  };

  const onConfirmarModelo = (modelo) => {
    setCampos(fields => ({
      ...fields,
      texto: modelo.texto,
      assunto: modelo.assunto,
      idTipoEmailPadrao: modelo.idTipoEmailPadrao,
      solicitarFase: modelo.solicitarFase,
      nomeModelo: modelo.nomeModelo,
    }));
    setErros(camposErro => ({ ...camposErro, texto: undefined, assunto: undefined }));
  };

  const onSubmit = useCallback(() => {
    // Guarda síncrona: `enviando` (state) só reflete o novo valor no próximo
    // render, então 2 cliques disparados antes desse re-render ainda veem o
    // closure antigo de `onSubmit` com `enviando === false` e enviariam
    // 2 vezes — `enviandoRef` bloqueia isso imediatamente (achado em code
    // review).
    if (enviandoRef.current) return;
    enviandoRef.current = true;
    setEnviando(true);
    const sessaoAtual = sessaoRef.current;
    const idsProcessosNumericos = processosSelecionados.map(id => parseInt(id, 10));
    submitFormEnvioEmailEmMassa(idsProcessosNumericos, campos, ANEXOS_VAZIO).then(() => {
      if (sessaoRef.current !== sessaoAtual) return;
      onFeedback('success', 'E-mails enviados com sucesso!');
      onClose();
    }).catch((err) => {
      if (sessaoRef.current !== sessaoAtual) return;
      const validations = err.response?.data?.validations;
      const mensagem = err.response?.data?.message;
      if (validations?.length) {
        setErros(validations.reduce((acc, validation) => ({
          ...acc,
          [validation.field.replace(/^dadosEmailDevedorCommand\./, '')]: validation.message,
        }), {}));
      } else if (mensagem?.includes(MENSAGEM_HISTORICO_PROCESSOS)) {
        onFeedback('warning', mensagem);
        onClose();
      } else {
        onFeedback('error', mensagem || MENSAGEM_ERRO_GENERICA);
      }
    }).finally(() => {
      enviandoRef.current = false;
      if (sessaoRef.current === sessaoAtual) setEnviando(false);
    });
  }, [campos, processosSelecionados, onClose, onFeedback]);

  const confirmarDesabilitado = enviando || !campos.emailDe || !campos.assunto || !campos.texto;
  const mostrarNomeAdvogado = campos.texto?.includes(TOKEN_NOME_ADVOGADO);
  const mostrarEmailAdvogado = campos.texto?.includes(TOKEN_EMAIL_ADVOGADO);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Enviar E-mail de Notificação de Cobrança</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Field
            label="De*"
            value={campos.emailDe}
            disabled
            error={Boolean(erros.emailDe)}
            helperText={erros.emailDe}
          />
          <Field
            label="Cópia"
            value={campos.emailCopia}
            onChange={onChangeCampo('emailCopia')}
            error={Boolean(erros.emailCopia)}
            helperText={erros.emailCopia}
          />
          <Field
            label="Resposta para:"
            value={campos.respostaPara}
            onChange={onChangeCampo('respostaPara')}
            error={Boolean(erros.respostaPara)}
            helperText={erros.respostaPara}
          />
          <Field
            label="Assunto*"
            value={campos.assunto}
            onChange={onChangeCampo('assunto')}
            error={Boolean(erros.assunto)}
            helperText={erros.assunto}
          />
          {mostrarNomeAdvogado && (
            <Field
              label="Nome Advogado*"
              value={campos.nomeAdvogado}
              onChange={onChangeCampo('nomeAdvogado')}
              error={Boolean(erros.nomeAdvogado)}
              helperText={erros.nomeAdvogado}
            />
          )}
          {mostrarEmailAdvogado && (
            <Field
              label="Email Advogado*"
              value={campos.emailAdvogado}
              onChange={onChangeCampo('emailAdvogado')}
              error={Boolean(erros.emailAdvogado)}
              helperText={erros.emailAdvogado}
            />
          )}
          <Field
            label="Texto*"
            value={campos.texto}
            InputProps={{ readOnly: true }}
            multiline
            minRows={6}
            error={Boolean(erros.texto)}
            helperText={erros.texto}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Button onClick={() => setOpenEmailsPadrao(true)}>Emails Padrão</Button>
        <Box>
          <Button onClick={limparFormulario}>Limpar</Button>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" disabled={confirmarDesabilitado} onClick={onSubmit}>Enviar</Button>
        </Box>
      </DialogActions>

      <EmailsPadraoDialog
        open={openEmailsPadrao}
        onClose={() => setOpenEmailsPadrao(false)}
        onConfirmar={onConfirmarModelo}
      />
    </Dialog>
  );
}

DialogEnviarEmailDevedor.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFeedback: PropTypes.func.isRequired,
  processosSelecionados: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
};
